import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { useParams } from "react-router-dom";
import { NotFoundError } from "../../api/errors";
import {
  apiShape,
  backendShape,
  useBrowser,
  useNotifier,
  useRules,
  withApi,
  withBackend,
} from "../../context";
import {
  extractAssets,
  extractInstrumentation,
  extractInstrumentationStatuses,
  extractInstrumentationTypes,
  isUserInGroup,
} from "../../extractors";
import { useAccessRightsQuery, useApi } from "../../hooks";
import { packInstrumentationSpecifications } from "../../packmans";
import { userShape } from "../../shapes";
import {
  convertLabelToSpecificationKey,
  convertSpecificationKeyToLabel,
  extractCode,
  parseIntlNumber,
} from "../../utils";
import { withUser } from "../../wrappers";
import ActionBar from "../ActionBar/ActionBar";
import BackButton from "../BackButton";
import { Column, Container, Row } from "../Grid";
import Heading from "../Heading";
import Loader from "../Loader";
import PermissionsEdit from "../Permissions/PermissionsEdit";
import EditPictures from "../Pictures/EditPictures";
import { PROJECT_ENGINEER_ROLE } from "../Specifications/SpecificationConstants";
import InstrumentationForm from "./InstrumentationForm";

type Props = {
  readonly user: userShape;
  readonly api: apiShape;
  readonly backend: backendShape;
};

type ExtendedOrderCodeResponse = {
  extended_order_code?: string;
};

export function InstrumentationEdit({ user, api, backend }: Props) {
  const intl = useIntl();
  const rules = useRules();
  const browser = useBrowser();
  const notifier = useNotifier();
  const params = useParams<{ id: string }>();
  const instrumentationId = params.id;
  const { data: accessRights } = useAccessRightsQuery("Instrumentation", {
    enabled: !!instrumentationId,
  });

  const apiHook = useApi();

  const [instrumentationDetails, setInstrumentationDetails] = useState();

  const primaryAsset = instrumentationDetails
    ? (instrumentationDetails as $TSFixMe).assets[0]
    : undefined;

  const orderCodeLookUp =
    primaryAsset?.manufacturerName === "Endress+Hauser" &&
    primaryAsset?.specifications?.["eh.pcps.tmp.ordercode"] &&
    /^[a-zA-Z0-9]+-+[a-zA-Z0-9]+\/[a-zA-Z0-9]+/g.exec(
      primaryAsset?.specifications["eh.pcps.tmp.ordercode"].value,
    );

  const orderCode =
    primaryAsset?.specifications?.["eh.pcps.tmp.ordercode"]?.value;

  const shouldFetchEoc = !!orderCodeLookUp && !!orderCode;

  // Extended order code is required to derive if a product is FWR30 or FWR30v10
  const { data: extendedOrderCodeResponse } =
    apiHook.get.useQuery<ExtendedOrderCodeResponse>(
      "/endress/extended_order_code_lookup",
      { order_code: orderCode },
      {
        enabled: shouldFetchEoc,
        retry: false,
        meta: { useGlobalErrorHandling: false },
      },
    );

  useEffect(() => {
    if (extendedOrderCodeResponse?.extended_order_code) {
      setInstrumentationDetails((prev: $TSFixMe) => {
        const extendedOrderCode = extractCode(
          extendedOrderCodeResponse?.extended_order_code,
        );
        /* istanbul ignore next */
        if (!prev?.assets?.[0]) return prev;

        const updatedAssets = [...prev.assets];
        updatedAssets[0] = {
          ...updatedAssets[0],
          extendedOrderCode,
        };

        return {
          ...prev,
          assets: updatedAssets,
        };
      });
    }
  }, [extendedOrderCodeResponse]);

  const instrumentationRules = instrumentationDetails
    ? rules.instrumentation(
        (instrumentationDetails as $TSFixMe).instrumentation,
        (instrumentationDetails as $TSFixMe).assets,
      )
    : undefined;

  const isUserProjectEngineer = isUserInGroup(user, PROJECT_ENGINEER_ROLE);

  const collapseProperties = rules
    .application()
    .get("shouldCollapseProperties");

  const onSubmit = async (values: $TSFixMe, actions: $TSFixMe) => {
    let packSpecificationValues = instrumentationRules.get(
      "needsFullEmptySettings",
    )
      ? {
          ...values,
          full: parseIntlNumber(values.full).toString(),
          empty: parseIntlNumber(values.empty).toString(),
          blockDistance: parseIntlNumber(values.blockDistance).toString(),
          sensitivity: values.sensitivity.id,
        }
      : values;

    /* istanbul ignore else */
    if (values.mediumType) {
      packSpecificationValues = {
        ...packSpecificationValues,
        mediumType: values.mediumType.id,
      };
    }

    try {
      const patchInstrumentation = {
        tag: values.tag,
        description: values.description,
        status: { id: values.status.id },
        type: { id: values.type.id },
        criticality: values.criticality.id.toLowerCase(),
        accessibility: values.accessibility.id.toLowerCase(),
      };
      /* istanbul ignore next */
      if (
        !values.type.tenantPublic &&
        values.tenantId !== values.type.tenantId
      ) {
        // FIXME: this assigns the tenant of the type when creating a new instrumentation. We need real tenant handling instead here!
        (patchInstrumentation as $TSFixMe).tenant = {
          id: values.type.tenantId,
        };
      }

      await api.patch(
        `/instrumentations/${instrumentationId}`,
        patchInstrumentation,
      );

      const specifications = packInstrumentationSpecifications(
        packSpecificationValues,
      );

      values.specifications?.forEach((specification: $TSFixMe) => {
        if (specification.key !== "" && specification.key?.name) {
          specifications[
            convertLabelToSpecificationKey(specification.key.name)
          ] = {
            value: specification.value,
            ui_visible: isUserProjectEngineer
              ? !!specification.uiVisible
              : true,
          };
        }
      });

      await api.patch(
        `/instrumentations/${instrumentationId}/specifications`,
        specifications,
      );

      // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
      if (instrumentationDetails.initialSpecificationKeys?.length > 0) {
        const specificationKeysToDelete =
          // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
          instrumentationDetails.initialSpecificationKeys.filter(
            (key: $TSFixMe) =>
              !values.specifications.find(
                (specification: $TSFixMe) =>
                  specification.key !== "" && specification.key?.id === key,
              ),
          );
        if (specificationKeysToDelete.length > 0) {
          await api.delete(
            `/instrumentations/${instrumentationId}/specifications`,
            specificationKeysToDelete,
          );
        }
      }

      if (instrumentationRules.get("needsFullEmptySettings")) {
        await backend.patch(`/connected_instrumentations/${instrumentationId}`);
      }

      actions.setSubmitting(false);
      actions.resetForm({ values });
      notifier.showSuccess(
        intl.formatMessage({ id: "instrumentation_edit.success_notification" }),
      );
      browser.navigateTo(`/instrumentations/${instrumentationId}`);
    } catch (error) {
      actions.setSubmitting(false);
      notifier.showError(api.translateError(error));
    }
  };

  const loadData = async () => {
    try {
      const tag = await api.get(
        `/instrumentations/${instrumentationId}`,
        { include: "type,status,specifications" },
        false,
      );
      const assets = await api.getAll(
        `/instrumentations/${instrumentationId}/assets`,
        {
          include:
            "product,product.tenant,product.manufacturer.tenant,specifications",
        },
      );
      const tagStatuses = await api.getAll(
        `/instrumentations/${instrumentationId}/status-options`,
        { include: "tenant" },
      );
      const tagTypes = await api.getAll(
        `/instrumentations/${instrumentationId}/type-options`,
        { include: "tenant" },
      );
      const extractedAssets = extractAssets(assets);
      let firmwareVersion;
      let sensorBuildNumber;
      if (extractedAssets.length > 0) {
        const firstAsset = extractedAssets[0];
        const firmwareLoaded = await api.get(
          `/assets/${firstAsset.id}/softwares`,
          { software_type_id: 1 },
        );
        if (
          firmwareLoaded &&
          firmwareLoaded.softwares &&
          firmwareLoaded.softwares.length > 0
        ) {
          firmwareVersion = firmwareLoaded.softwares[0].version_number;
        }
        sensorBuildNumber = firstAsset.sensorModuleBuildNumber;
      }
      const tagRules = rules.instrumentation(tag, extractedAssets);
      const extractedTag = extractInstrumentation(tag);

      (extractedTag as $TSFixMe).unit = tagRules
        ?.get("fullEmptyCalibrationUnitOptions")
        ?.filter(
          (unit: $TSFixMe) => unit.id === (extractedTag as $TSFixMe).unit,
        )[0];

      const initialSpecificationKeys = tag.specifications
        ? Object.keys(tag.specifications)
            .filter(
              (key) =>
                isUserProjectEngineer || tag.specifications[key].ui_visible,
            )
            .sort((a, b) => {
              const aVisible = tag.specifications[a].ui_visible;
              const bVisible = tag.specifications[b].ui_visible;

              // Sort by ui_visible first (true comes before false)
              if (aVisible !== bVisible) {
                return aVisible ? -1 : 1;
              }

              // Then sort alphabetically by key
              return a.localeCompare(b);
            })
        : [];
      extractedTag.specifications = initialSpecificationKeys.map((key) => ({
        key: { id: key, name: convertSpecificationKeyToLabel(key) },
        value: tag.specifications[key].value,
        uiVisible: tag.specifications[key].ui_visible,
      }));

      setInstrumentationDetails({
        // @ts-expect-error TS(2345) FIXME: Argument of type '{ accessRights: accessRightsShap... Remove this comment to see the full error message
        accessRights,
        assets: extractedAssets,
        firmwareVersion,
        initialSpecificationKeys,
        instrumentation: extractedTag,
        sensorBuildNumber,
        statuses: extractInstrumentationStatuses(tagStatuses),
        types: extractInstrumentationTypes(tagTypes, true),
      });
    } catch (error) {
      if (error instanceof NotFoundError) {
        browser.navigateTo("/404");
      } else {
        notifier.showError(api.translateError(error));
      }
    }
  };

  useEffect(() => {
    loadData();
  }, [params]);

  const editImages = instrumentationDetails ? (
    <EditPictures
      // @ts-expect-error TS(2322) FIXME: Type '{ id: string; model: string; modelId: any; p... Remove this comment to see the full error message
      id="edit-pictures"
      model="instrumentations"
      modelId={(instrumentationDetails as $TSFixMe).instrumentation.id}
      placeholder="lcm-iot-icon-instrumentation"
    />
  ) : null;
  const editPermissions = instrumentationDetails ? (
    <PermissionsEdit
      // @ts-expect-error TS(2322) FIXME: Type '{ id: string; permitableType: string; permit... Remove this comment to see the full error message
      id="edit-permissions"
      permitableType="Instrumentation"
      permitableId={(instrumentationDetails as $TSFixMe).instrumentation.id}
      accessRights={accessRights}
      targetOnDeleteOwnReadPermission="/nodes"
    />
  ) : null;
  const editInstrumentation = instrumentationDetails ? (
    <InstrumentationForm
      // @ts-expect-error TS(2322) FIXME: Type '{ id: string; intl: intlShape; instrumentati... Remove this comment to see the full error message
      id="instrumentation-form"
      intl={intl}
      instrumentationStatuses={(instrumentationDetails as $TSFixMe).statuses}
      instrumentationTypes={(instrumentationDetails as $TSFixMe).types}
      assetFirmwareVersion={
        (instrumentationDetails as $TSFixMe).firmwareVersion
      }
      assetSensorBuildNumber={
        (instrumentationDetails as $TSFixMe).sensorBuildNumber
      }
      onSubmit={onSubmit}
      instrumentation={(instrumentationDetails as $TSFixMe).instrumentation}
      instrumentationRules={instrumentationRules}
      collapseProperties={collapseProperties}
      isUserProjectEngineer={isUserProjectEngineer}
    />
  ) : null;

  return (
    <Container>
      <Row>
        <Column>
          <BackButton />
        </Column>
      </Row>
      <Row>
        <Column>
          <ActionBar>
            <Heading
              id="instrumentation-edit-header"
              level={1}
              title={intl.formatMessage({ id: "instrumentation_edit.header" })}
            />
          </ActionBar>
        </Column>
      </Row>
      <Row>
        <Column lg="7">
          <Heading
            id="instrumentation-edit-details"
            level={2}
            title={intl.formatMessage({ id: "instrumentation_edit.details" })}
          />
          {editInstrumentation}
        </Column>
        <Column lg="5">{editImages}</Column>
      </Row>
      <Row>
        <div>{editPermissions}</div>
      </Row>
      <Row>
        <div>
          <Loader loading={!instrumentationDetails} />
        </div>
      </Row>
    </Container>
  );
}

export default withApi(withBackend(withUser(InstrumentationEdit)));
